import { Button } from 'components/button/Button';
import { InputField } from 'components/input/InputField';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { useSWRConfig } from 'swr';
import * as Yup from 'yup';

import { updateProductType, useProductTypesSuspense, PRODUCT_TYPES_SWR_KEY } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { nullthrows } from '../../../utils/invariant';
import { captureException } from '@sentry/react';
import { DelayedSpinner } from 'components/Spinner';

type ProductTypeItem = NonNullable<ReturnType<typeof useProductTypesSuspense>['data']>[number];

const createProductTypeSchema = Yup.object().shape({
  id: Yup.string().required('Vereist'),
  nameNl: Yup.string().required('Vereist'),
  nameFr: Yup.string().required('Vereist'),
  nameEn: Yup.string().required('Vereist'),
  nameDe: Yup.string().required('Vereist'),
});

interface IProductTypeValues {
  id: string;
  nameNl: string;
  nameFr: string;
  nameEn: string;
  nameDe: string;
}

interface IUpdateProductTypePageProps {
  id: string;
  onComplete?: (values: IProductTypeValues) => void;
  onCancel?: () => void;
}

export const UpdateProductTypeForm: React.FC<IUpdateProductTypePageProps> = (props) => {
  const { id, onComplete, onCancel } = props;
  const { data, isLoading } = useProductTypesSuspense();

  if (isLoading) {
    return <DelayedSpinner size={8} className="flex justify-center items-center p-8" />;
  }

  const productType = nullthrows(
    data?.find((v) => v.id === id),
    'Cannot find product type',
  );
  return <ProductTypeForm productType={productType} onComplete={onComplete} onCancel={onCancel} />;
};

const ProductTypeForm: React.FC<{
  productType: ProductTypeItem;
  onComplete?: (values: IProductTypeValues) => void;
  onCancel?: () => void;
}> = ({ productType, onComplete, onCancel }) => {
  const { mutate } = useSWRConfig();

  const initialValues: IProductTypeValues = useMemo(() => {
    return {
      id: productType.id,
      nameNl: productType.nameNl,
      nameFr: productType.nameFr,
      nameEn: productType.nameEn,
      nameDe: productType.nameDe,
    };
  }, [productType]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={createProductTypeSchema}
      onSubmit={async (values) => {
        try {
          const patch: Partial<typeof values> = diff(initialValues, values);
          await updateProductType(productType.id, patch);
          await mutate(PRODUCT_TYPES_SWR_KEY);
          onComplete?.(values);
          toast.success('Verpakking type aangepast');
        } catch (err: any) {
          captureException(err);
          toast.error('Kon product type niet aanpassen: ' + getDisplayError(err));
        }
      }}
    >
      {({ handleSubmit, isSubmitting, submitForm }) => {
        return (
          <form onSubmit={handleSubmit}>
            <InputField labelText="Code" type="text" name="id" />
            <InputField labelText="Naam NL" type="text" name="nameNl" />
            <InputField labelText="Naam EN" type="text" name="nameEn" />
            <InputField labelText="Naam FR" type="text" name="nameFr" />
            <InputField labelText="Naam DE" type="text" name="nameDe" />

            <div className="flex justify-between">
              {onCancel && (
                <div>
                  <Button isDisabled={isSubmitting} isLoading={isSubmitting} onTrigger={onCancel}>
                    Annuleer
                  </Button>
                </div>
              )}
              <div>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  onTrigger={submitForm}
                >
                  Pas product type aan
                </Button>
              </div>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
